import type { IdsReport } from '@manudev/model';
import { failuresOf, tidy } from './verdicts';

export interface ReportMeta {
  ruleSet: string;
  model: string;
  checkedAt: string;
}

// the page's colours and fonts, copied into the HTML report so it looks like the site
export const REPORT_VARS = [
  '--bg',
  '--fg',
  '--muted',
  '--accent',
  '--accent-2',
  '--border',
  '--font-sans',
  '--font-mono',
];

export function reportJson(report: IdsReport, meta: ReportMeta): string {
  return JSON.stringify(
    {
      ...meta,
      specifications: report.specifications.map((s) => ({
        name: s.name,
        applicable: s.applicable,
        passed: s.passed,
        failed: failuresOf(s).length,
        passedIds: s.passedIds,
        failures: s.failed,
      })),
    },
    null,
    2,
  );
}

export function reportHtml(
  report: IdsReport,
  meta: ReportMeta,
  vars: Record<string, string>,
): string {
  const root = Object.entries(vars)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}: ${v};`)
    .join(' ');
  const specs = report.specifications
    .map((s) => {
      const rows = failuresOf(s)
        .map(
          (f) =>
            `<tr><td class="mono">${esc(f.globalId)}</td><td>${esc(f.name || '–')}</td><td>${f.requirements.map(esc).join('<br>')}</td><td class="muted">${f.reasons.map((r) => esc(tidy(r))).join('<br>')}</td></tr>`,
        )
        .join('\n');
      return `<section>
<h2>${esc(s.name)}</h2>
<p class="counts">${s.applicable} applicable · <span class="pass">${s.passed} passed</span> · <span class="fail">${failuresOf(s).length} failed</span></p>
${rows ? `<table><thead><tr><th>GlobalId</th><th>Element</th><th>Requirement</th><th>Reason</th></tr></thead><tbody>\n${rows}\n</tbody></table>` : ''}
</section>`;
    })
    .join('\n');
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(meta.ruleSet)} · IDS check</title>
<style>
:root { ${root} color-scheme: dark; }
body { margin: 0; background: var(--bg, #07080a); color: var(--fg, #e8e6e1); font: 14px/1.5 var(--font-sans, system-ui, sans-serif); }
main { max-width: 960px; margin: 0 auto; padding: 32px 16px 48px; }
h1 { margin: 0 0 4px; font-size: 22px; }
h2 { margin: 28px 0 4px; font-size: 16px; color: var(--accent-2, #4cc9f0); }
.meta, .muted { color: var(--muted, #8b8f98); }
.counts { margin: 0 0 8px; font-family: var(--font-mono, ui-monospace, monospace); font-size: 12px; }
.pass { color: #3fae6a; }
.fail { color: #e0453a; }
table { width: 100%; border-collapse: collapse; font-size: 12px; }
th, td { text-align: left; vertical-align: top; padding: 5px 8px; border-top: 1px solid var(--border, #222); }
th { color: var(--muted, #8b8f98); font-weight: 500; }
.mono { font-family: var(--font-mono, ui-monospace, monospace); white-space: nowrap; }
</style>
</head>
<body>
<main>
<h1>${esc(meta.ruleSet)}</h1>
<p class="meta">${esc(meta.model)} · checked ${esc(meta.checkedAt)} in the browser</p>
${specs}
</main>
</body>
</html>
`;
}

const esc = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
